"use strict";
'use es6';

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard");

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = TwoWayTransition;

var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));

var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _react = _interopRequireWildcard(require("react"));

var _useMounted = _interopRequireDefault(require("react-utils/hooks/useMounted"));

var _styledComponents = _interopRequireDefault(require("styled-components"));

var _componentProp = require("../utils/propTypes/componentProp");

var activeContextValue = {
  transitioning: true
};
var inactiveContextValue = {
  transitioning: false
};

var getContextValue = function getContextValue(transitioning) {
  return transitioning ? activeContextValue : inactiveContextValue;
};

function TwoWayTransition(props) {
  var children = props.children,
      ContextProvider = props.ContextProvider,
      duration = props.duration,
      onCloseComplete = props.onCloseComplete,
      onCloseStart = props.onCloseStart,
      onOpenComplete = props.onOpenComplete,
      onOpenStart = props.onOpenStart,
      transitionOnMount = props.transitionOnMount,
      transitionOnUpdate = props.transitionOnUpdate,
      Wrapper = props.Wrapper,
      rest = (0, _objectWithoutProperties2.default)(props, ["children", "ContextProvider", "duration", "onCloseComplete", "onCloseStart", "onOpenComplete", "onOpenStart", "transitionOnMount", "transitionOnUpdate", "Wrapper"]);
  /** An update that changes `children` between null and non-null triggers open/close */

  var nextOpen = (0, _componentProp.isRenderable)(children);

  var _useState = (0, _react.useState)(transitionOnMount ? !nextOpen : nextOpen),
      _useState2 = (0, _slicedToArray2.default)(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  var _useState3 = (0, _react.useState)(false),
      _useState4 = (0, _slicedToArray2.default)(_useState3, 2),
      transitioning = _useState4[0],
      setTransitioning = _useState4[1];

  var _useState5 = (0, _react.useState)(false),
      _useState6 = (0, _slicedToArray2.default)(_useState5, 2),
      transitionActive = _useState6[0],
      setTransitionActive = _useState6[1];
  /** A ref to a copy of `children` that's frozen during the close transition */


  var cachedChildrenRef = (0, _react.useRef)(children);
  if (nextOpen) cachedChildrenRef.current = children;
  var timeoutHandleRef = (0, _react.useRef)(null);
  var rAFHandleRef = (0, _react.useRef)(null);
  var mountedRef = (0, _useMounted.default)();

  var clearTimeouts = function clearTimeouts() {
    clearTimeout(timeoutHandleRef.current);
    cancelAnimationFrame(rAFHandleRef.current);
  };

  var startTransition = function startTransition() {
    clearTimeouts();

    if (nextOpen) {
      if (onOpenStart) onOpenStart();
    } else {
      if (onCloseStart) onCloseStart();
    }

    var stopTransition = function stopTransition() {
      if (nextOpen) {
        if (onOpenComplete) onOpenComplete();
      } else {
        if (onCloseComplete) onCloseComplete();
      }

      if (!mountedRef.current) return;
      if (!nextOpen) cachedChildrenRef.current = null;
      setTransitioning(false);
      setTransitionActive(false);
    }; // If duration={0}, skip the CSS transition entirely (#6542)


    if (duration === 0) {
      setOpen(nextOpen);
      stopTransition();
      return;
    }

    setTransitioning(true);
    rAFHandleRef.current = requestAnimationFrame(function () {
      rAFHandleRef.current = requestAnimationFrame(function () {
        setOpen(nextOpen);
        setTransitionActive(true);
        timeoutHandleRef.current = setTimeout(stopTransition, duration);
      });
    });
  }; // Mount/unmount logic


  (0, _react.useEffect)(function () {
    if (transitionOnMount && !transitionOnUpdate) {
      startTransition();
    } else if (open && !transitionOnMount) {
      // Some logic requires "open" events to trigger, even when the transition is skipped.
      if (onOpenStart) onOpenStart();
      if (onOpenComplete) onOpenComplete();
    }

    return function () {
      clearTimeouts();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Open/close logic

  (0, _react.useEffect)(function () {
    if (transitionOnUpdate && open !== nextOpen) {
      startTransition();
    }
  }, [open, nextOpen, transitionOnUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  var renderedWrapper = _react.default.createElement(Wrapper, Object.assign({}, rest, {
    duration: duration,
    open: open,
    opening: transitionActive && open,
    closing: transitionActive && !open,
    transitioning: transitioning,
    transitionActive: transitionActive
  }), cachedChildrenRef.current);

  return ContextProvider ? _react.default.createElement(ContextProvider, {
    value: getContextValue(transitioning)
  }, renderedWrapper) : renderedWrapper;
}

if (process.env.NODE_ENV !== 'production') {
  TwoWayTransition.propTypes = {
    children: _propTypes.default.node,
    ContextProvider: _propTypes.default.elementType,
    duration: _propTypes.default.number.isRequired,
    onCloseComplete: _propTypes.default.func,
    onCloseStart: _propTypes.default.func,
    onOpenComplete: _propTypes.default.func,
    onOpenStart: _propTypes.default.func,
    transitionOnMount: _propTypes.default.bool.isRequired,
    transitionOnUpdate: _propTypes.default.bool.isRequired,
    Wrapper: (0, _componentProp.getComponentPropType)({
      propTypes: {
        duration: _propTypes.default.number,
        open: _propTypes.default.bool.isRequired,
        transitioning: _propTypes.default.bool.isRequired,
        transitionActive: _propTypes.default.bool.isRequired,
        closing: _propTypes.default.bool.isRequired,
        opening: _propTypes.default.bool.isRequired
      }
    }).isRequired
  };
}

TwoWayTransition.defaultProps = {
  transitionOnMount: false,
  transitionOnUpdate: true
};
TwoWayTransition.displayName = 'TwoWayTransition';

var PlainWrapper = _styledComponents.default.div.attrs({
  'data-transition-wrapper': true
}).withConfig({
  displayName: "TwoWayTransition__PlainWrapper",
  componentId: "xdsd7u-0"
})([""]);

PlainWrapper.displayName = 'TwoWayTransition.PlainWrapper';
TwoWayTransition.PlainWrapper = PlainWrapper;
module.exports = exports.default;